// Composant RegistrationList nettoyé (sans doublons JSX invalides)
import React, { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  OutlinedInput,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  fetchAllRegistrations,
  getAllRegistrations,
  getAllRegistrationsLoading,
  changeParticipationPaymentStatus,
  fetchBoards,
} from "../../../features/tournoi/tournoiSlice";
import { Participation } from "../../../types";
import { arrayToCsv, createCsvBlob } from "../../../utils/arrayHelper";

const RegistrationList: FC = () => {
  const dispatch = useAppDispatch();
  const registrations = useAppSelector(getAllRegistrations);
  const loading = useAppSelector(getAllRegistrationsLoading);

  const [sortField, setSortField] = useState<string | null>("date");
  const [sortAsc, setSortAsc] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState<string>("all");
  const [checkinFilter, setCheckinFilter] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isOpenedConfirmModal, setIsOpenedConfirmModal] = useState(false);
  const [selectedParticipation, setSelectedParticipation] =
    useState<Participation | null>(null);

  useEffect(() => {
    const load = async () => {
      await dispatch(fetchBoards());
      await dispatch(fetchAllRegistrations());
    };
    load();
  }, [dispatch]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const handleExport = () => {
    const rows = filteredRegistrations.map((p) => [
      `${p.players.name} ${p.players.surname}`,
      p.players.dossard || "",
      p.players.points,
      p.players.licenceId,
      p.players.club,
      p.players.categorie,
      p.players.sexe,
      p.tableau.name,
      p.email,
      new Date(p.dateInscription).toLocaleString("fr-FR"),
      getCheckinStatus(p),
      p.checkedInAt &&
      p.checkedInAt instanceof Date &&
      !isNaN(p.checkedInAt.getTime())
        ? p.checkedInAt.toLocaleString("fr-FR")
        : p.checkedInAt &&
          typeof p.checkedInAt === "object" &&
          "toDate" in p.checkedInAt &&
          typeof p.checkedInAt.toDate === "function"
        ? p.checkedInAt.toDate().toLocaleString("fr-FR")
        : "",
      paymentStatusLabel(p.paymentStatus),
    ]);

    const headers = [
      "Nom",
      "Dossard",
      "Points",
      "Licence",
      "Club",
      "Catégorie",
      "Sexe",
      "Tableau",
      "Email",
      "Date d'inscription",
      "Pointage",
      "Date de pointage",
      "Paiement",
    ];

    const blob = createCsvBlob(headers, rows);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `inscriptions_${new Date().toISOString()}.csv`;
    link.click();
  };

  const paymentStatusLabel = (status: string | undefined) => {
    if (!status) return "Non payé";
    switch (status) {
      case "registration":
        return "Payé à l'inscription";
      case "admin":
        return "Payé";
      case "cancelled":
        return "Annulé";
      default:
        return "Non payé";
    }
  };

  const isCancellation = (participation: Participation) =>
    participation.paymentStatus === "registration" ||
    participation.paymentStatus === "admin";

  const handlePaymentChangeClick = (participation: Participation) => {
    setSelectedParticipation(participation);
    setIsOpenedConfirmModal(true);
  };

  const handleConfirmationChange = async () => {
    if (selectedParticipation) {
      const status = isCancellation(selectedParticipation)
        ? "cancelled"
        : "admin";
      await dispatch(
        changeParticipationPaymentStatus({
          participationId: selectedParticipation.id,
          status,
        })
      );

      setIsOpenedConfirmModal(false); // Fermez la boîte de dialogue après avoir traité l'action
    }
  };

  // Fonction pour gérer les 4 valeurs de pointage (définie avant son utilisation)
  const getCheckinStatus = (participation: Participation): string => {
    if (participation.isPostClosureRegistration) {
      return participation.checkedIn ? "Nouveau pointé" : "Nouveau Non pointé";
    } else {
      return participation.checkedIn ? "Pointé" : "Non pointé";
    }
  };

  const checkinStatusOptions = [
    "Pointé",
    "Non pointé",
    "Nouveau pointé",
    "Nouveau Non pointé",
  ];

  const handleCheckinFilterChange = (event: any) => {
    const value = event.target.value;
    setCheckinFilter(typeof value === "string" ? value.split(",") : value);
  };

  const filteredRegistrations = useMemo(() => {
    return registrations.filter((p) => {
      const player = p.players;
      if (!player) return false;

      // Filtre par recherche
      const searchMatch =
        player.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        player.surname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        player.licenceId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (player.dossard && player.dossard.toString().includes(searchTerm));

      // Filtre par paiement
      const paymentMatch =
        paymentFilter === "all" ||
        (paymentFilter === "paid" &&
          (p.paymentStatus === "admin" ||
            p.paymentStatus === "registration")) ||
        (paymentFilter === "unpaid" &&
          (p.paymentStatus === undefined || p.paymentStatus === "cancelled"));

      // Filtre par pointage
      const checkinMatch =
        checkinFilter.length === 0 ||
        checkinFilter.includes(getCheckinStatus(p));

      return searchMatch && paymentMatch && checkinMatch;
    });
  }, [registrations, paymentFilter, checkinFilter, searchTerm]);

  const sortedRegistrations = useMemo(() => {
    if (!sortField) return filteredRegistrations;
    return [...filteredRegistrations].sort((a, b) => {
      const get = (p: Participation) => {
        switch (sortField) {
          case "name":
            return `${p.players.name} ${p.players.surname}`;
          case "dossard":
            return p.players.dossard || 0;
          case "points":
            return Number(p.players.points);
          case "licence":
            return p.players.licenceId || "";
          case "club":
            return p.players.club || "";
          case "categorie":
            return p.players.categorie || "";
          case "sexe":
            return p.players.sexe || "";
          case "tableau":
            return p.tableau.name || "";
          case "email":
            return p.email || "";
          case "date":
            return new Date(p.dateInscription).getTime();
          case "pointage":
            return getCheckinStatus(p);
          case "datePointage":
            return p.checkedInAt &&
              p.checkedInAt instanceof Date &&
              !isNaN(p.checkedInAt.getTime())
              ? p.checkedInAt.getTime()
              : p.checkedInAt &&
                typeof p.checkedInAt === "object" &&
                "toDate" in p.checkedInAt &&
                typeof p.checkedInAt.toDate === "function"
              ? p.checkedInAt.toDate().getTime()
              : 0;
          case "payment":
            return paymentStatusLabel(p.paymentStatus);
          default:
            return "";
        }
      };
      const valA = get(a);
      const valB = get(b);
      return (valA > valB ? 1 : -1) * (sortAsc ? 1 : -1);
    });
  }, [filteredRegistrations, sortField, sortAsc]);

  // Vérifier si au moins un joueur a un dossard
  const hasAnyDossard = useMemo(() => {
    return registrations.some((p) => p.players.dossard);
  }, [registrations]);

  if (loading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: "80vh" }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: "100%",
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 140px)",
        gap: 2,
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filtrer par paiement</InputLabel>
          <Select
            label="Filtrer par paiement"
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="paid">Payé</MenuItem>
            <MenuItem value="unpaid">Non payé</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filtrer par pointage</InputLabel>
          <Select
            multiple
            label="Filtrer par pointage"
            value={checkinFilter}
            onChange={handleCheckinFilterChange}
            input={<OutlinedInput label="Filtrer par pointage" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {checkinStatusOptions.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            label="Recherche"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 320, mb: 0 }}
            InputProps={{
              startAdornment: (
                <Box
                  component="span"
                  sx={{ pr: 1, display: "flex", alignItems: "center" }}
                >
                  🔍
                </Box>
              ),
              endAdornment: searchTerm ? (
                <Box
                  component="span"
                  sx={{
                    cursor: "pointer",
                    pl: 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                  onClick={() => setSearchTerm("")}
                >
                  ✖️
                </Box>
              ) : null,
            }}
          />
          {(paymentFilter !== "all" || searchTerm) && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ml: 1, whiteSpace: "nowrap" }}
            >
              {`${sortedRegistrations.length} inscription${
                sortedRegistrations.length > 1 ? "s" : ""
              } affichée${sortedRegistrations.length > 1 ? "s" : ""} sur ${
                registrations.length
              }`}
            </Typography>
          )}
        </Box>

        <Button variant="outlined" onClick={handleExport}>
          Exporter sous Excel
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ flex: 1, overflow: "auto" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {[
                { label: "Nom", field: "name" },
                ...(hasAnyDossard
                  ? [{ label: "Dossard", field: "dossard" }]
                  : []),
                { label: "Points", field: "points" },
                { label: "Licence", field: "licence" },
                { label: "Club", field: "club" },
                { label: "Catégorie", field: "categorie" },
                { label: "Sexe", field: "sexe" },
                { label: "Tableau", field: "tableau" },
                { label: "Email", field: "email" },
                { label: "Date d'inscription", field: "date" },
                { label: "Pointage", field: "pointage" },
                { label: "Date de pointage", field: "datePointage" },
                { label: "Paiement", field: "payment" },
                { label: "Action" },
              ].map((col) => (
                <TableCell
                  key={col.label}
                  onClick={col.field ? () => handleSort(col.field) : undefined}
                  sx={{
                    cursor: col.field ? "pointer" : "default",
                    fontWeight: 600,
                  }}
                >
                  {col.label}
                  {sortField === col.field && (sortAsc ? " ▲" : " ▼")}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRegistrations.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={10}
                  align="center"
                  sx={{ py: 4, fontStyle: "italic", color: "text.secondary" }}
                >
                  Aucune inscription trouvée.
                </TableCell>
              </TableRow>
            ) : (
              sortedRegistrations.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>{`${p.players.name} ${p.players.surname}`}</TableCell>
                  {hasAnyDossard && (
                    <TableCell>{p.players.dossard || ""}</TableCell>
                  )}
                  <TableCell>{p.players.points}</TableCell>
                  <TableCell>{p.players.licenceId}</TableCell>
                  <TableCell>{p.players.club}</TableCell>
                  <TableCell>{p.players.categorie}</TableCell>
                  <TableCell>{p.players.sexe}</TableCell>
                  <TableCell>{p.tableau.name}</TableCell>
                  <TableCell>{p.email}</TableCell>
                  <TableCell>
                    {new Date(p.dateInscription).toLocaleString("fr-FR")}
                  </TableCell>
                  <TableCell>{getCheckinStatus(p)}</TableCell>
                  <TableCell>
                    {p.checkedInAt &&
                    p.checkedInAt instanceof Date &&
                    !isNaN(p.checkedInAt.getTime())
                      ? p.checkedInAt.toLocaleString("fr-FR")
                      : p.checkedInAt &&
                        typeof p.checkedInAt === "object" &&
                        "toDate" in p.checkedInAt &&
                        typeof p.checkedInAt.toDate === "function"
                      ? p.checkedInAt.toDate().toLocaleString("fr-FR")
                      : ""}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: !p.paymentStatus
                            ? "grey.500"
                            : p.paymentStatus === "registration"
                            ? "info.main"
                            : p.paymentStatus === "admin"
                            ? "success.main"
                            : p.paymentStatus === "cancelled"
                            ? "error.main"
                            : "text.disabled",
                        }}
                      />
                      {paymentStatusLabel(p.paymentStatus)}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => handlePaymentChangeClick(p)}
                      sx={{ fontSize: "0.75rem", textTransform: "none" }}
                    >
                      {isCancellation(p)
                        ? "Annuler paiement"
                        : "Inscrire paiement"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {selectedParticipation && (
        <Dialog
          maxWidth="xs"
          onClose={() => setIsOpenedConfirmModal(false)}
          open={isOpenedConfirmModal}
        >
          <DialogContent>
            {isCancellation(selectedParticipation)
              ? "Voulez-vous vraiment marquer l'inscription comme non payée ?"
              : "Voulez-vous vraiment marquer l'inscription comme payée ?"}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsOpenedConfirmModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleConfirmationChange}>Ok</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default RegistrationList;
